import {createFormEditGuard} from '../services/assistantFormFill';
import useDiscoveryDialog from '../hooks/useDiscoveryDialog';
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { assistantRequest } from '../services/assistantApi';
import { getSaveContact } from '../services/propertySaveStorage';
import { homeAddress } from '../services/assistantDiscovery';
import './AssistantDiscovery.css';
const empty = {
  name: '',
  email: '',
  phone: '',
  date: '',
  time: ''
};
export default function ShowingRequest({
  listingKey,
  controlRef,
  onState,
  onResult,
  contact = {}
}) {
  const [open, setOpen] = useState(false),
    [draft, setDraft] = useState(empty),
    [review, setReview] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [result, setResult] = useState(null),
    [config, setConfig] = useState(null);
  const formGuard = useRef(createFormEditGuard());
  const submissionAttempted = useRef(false);
  const version = useRef(0),
    inFlight = useRef(false),
    live = useRef({});
  live.current = {
    draft,
    review,
    result,
    config,
    listingKey, open
  };
  useEffect(() => {
    const requestVersion = version;
    version.current++;
    formGuard.current.close();
    setOpen(false);
    setReview(null);
    setResult(null);
    submissionAttempted.current = false;
    setConfig(null);
    setDraft(empty);
    setBusy(false);
    inFlight.current = false;
    return () => {
      requestVersion.current++;
    };
  }, [listingKey]);
  useEffect(() => {
    onState?.({
      showing_open: open,
      showing_form: open ? formGuard.current.snapshot() : null,
      showing_submitting: busy && inFlight.current === 'submit',
      showing_ready: open && !!review && !busy && !result,
      showing_status: result ? 'sent' : busy ? 'working' : !open ? 'closed' : review ? 'ready' : 'draft',
      showing_missing_fields: open ? Object.keys(empty).filter(k => !draft[k].trim()) : [],
      showing_draft: open ? {
        ...draft,
        timezone: config?.timezone || ''
      } : {}
    });
  }, [open, review, busy, result, draft, config, onState]);
  const close = () => {
    formGuard.current.close();
    live.current.open=false;
    version.current++;
    inFlight.current = false;
    setBusy(false);
    setOpen(false);
    setReview(null);
    return 'Showing form closed. Your saved favorites are unchanged. Any request already submitted remains with the agent.';
  };
  function update(values, manual = false) {
    const next = {
      ...live.current.draft
    };
    Object.keys(empty).forEach(k => {
      if (typeof values[k] === 'string') {next[k] = values[k];if(manual)formGuard.current.manual(k);}
    });
    live.current = {
      ...live.current,
      draft: next,
      review: null
    };
    setDraft(next);
    setReview(null);
    setError('');
    return next;
  }
  async function openForm(params = {}) {
    formGuard.current.open();
    setOpen(true);
    setError('');
    if (live.current.result) return live.current.result.message || 'This showing request was already sent to the agent.';
    const savedContact = getSaveContact(contact);
    if (!live.current.result) update({
      ...Object.fromEntries(Object.entries({name:savedContact.name,email:savedContact.email,phone:savedContact.phoneNumber}).filter(([, v]) => v)),
      ...Object.fromEntries(Object.entries(live.current.draft).filter(([, v]) => v)),
      ...params
    });
    const current = version.current;
    try {
      const next = await assistantRequest('/discovery/config', {});
      if (current === version.current) setConfig(next);
    } catch (e) {
      if (current === version.current) setError(e.message);
    }
    if (current !== version.current) return 'The showing form was closed or changed. Nothing was submitted.';
    const missing = Object.keys(empty).filter(k => !live.current.draft[k].trim());
    return (missing.length ? 'Please provide ' + missing.join(', ') + '. ' : 'Your details are filled in. Review the request before sending. ') + 'A showing is optional; say skip for now to keep browsing. The agent must confirm availability.';
  }
  async function prepare(params = {}) {
    formGuard.current.open();
    if (inFlight.current) return 'The previous request is still running.';
    if (live.current.result) return 'This showing request has already been sent to the agent.';
    const next = update(params);
    setOpen(true);
    const missing = Object.keys(empty).filter(k => !next[k].trim());
    if (missing.length) return 'Please provide ' + missing.join(', ') + '.';
    inFlight.current = 'prepare';
    setBusy(true);
    const current = ++version.current;
    try {
      const preview = await assistantRequest('/showings/prepare', {
        listing_key: listingKey,
        ...next
      });
      if (current !== version.current) return 'The showing form changed. Please review the current details.';
      live.current = {
        ...live.current,
        review: preview
      };
      setReview(preview);
      setConfig({
        timezone: preview.timezone
      });
      return `Please confirm: request a showing of ${homeAddress(preview.home)} on ${preview.date} at ${preview.time}, ${preview.timezone}, for ${preview.name}, email ${preview.email}, phone ${preview.phone}. Say send the showing request, or use the confirmation button. This is not a confirmed appointment.`;
    } catch (e) {
      if (current === version.current) setError(e.message);
      return e.message;
    } finally {
      if (current === version.current) {
        inFlight.current = false;
        setBusy(false);
      }
    }
  }
  async function submit({confirm_current_draft = false, isCurrent = () => true} = {}) {
    if (inFlight.current) return 'The request is already running.';
    if (live.current.result) return live.current.result.message || 'This showing request has already been sent to the agent.';
    if (confirm_current_draft && !live.current.review && submissionAttempted.current) return 'The previous request may have been sent. Check its status with the agent before trying again.';
    if (confirm_current_draft && !live.current.review) {
      const approvedForm = formGuard.current.snapshot();
      if (!approvedForm || !isCurrent()) return 'The showing form changed. Nothing was sent.';
      const approvedDraft = JSON.stringify(live.current.draft);
      const prepared = await prepare();
      // Consent belongs to the displayed draft, not a replacement form or later edit.
      if (!isCurrent() || formGuard.current.snapshot()?.id !== approvedForm.id || JSON.stringify(live.current.draft) !== approvedDraft) return 'The showing details changed. Check the current form before sending.';
      if (!live.current.review) return prepared;
    }
    const preview = live.current.review;
    if (!preview) return 'Review the showing details before sending a request.';
    if (!isCurrent()) return 'The request changed. Nothing was sent.';
    submissionAttempted.current = true;
    inFlight.current = 'submit';
    setBusy(true);
    setError('');
    const current = ++version.current;
    // Remove the one-use preview before a network call so repeated commands cannot submit twice.
    live.current = {
      ...live.current,
      review: null
    };
    setReview(null);
    try {
      const sent = await assistantRequest('/showings/request', {
        ticket: preview.ticket
      });
      // Closing the form does not erase a successful submission for this home.
      if (live.current.listingKey !== listingKey) return sent.message;
      setResult(sent);
      live.current = {
        ...live.current,
        result: sent
      };
      return sent.message;
    } catch (e) {
      if (current === version.current) setError(e.message);
      return e.message;
    } finally {
      if (current === version.current) {
        inFlight.current = false;
        setBusy(false);
      }
    }
  }
  const dialogRef=useDiscoveryDialog(open,close);
  useImperativeHandle(controlRef, () => ({
    run: async params => {
      if (params.form_guard) {
        const fields=formGuard.current.filter(Object.fromEntries(Object.keys(empty).filter(key=>typeof params[key]==='string').map(key=>[key,params[key]])),params.form_guard);
        if (!fields) return 'The showing form changed. Nothing was filled in.';
        if (!Object.keys(fields).length) return 'I kept your manual corrections. Check the current showing details.';
        params={command:'update_showing',...fields};
      }
      if (params.command === 'cancel_showing') return close();
      if (params.command === 'open_showing') return openForm(params);
      if (params.command === 'submit_showing') return submit(params);
      return prepare(params);
    }
  }));
  if (!open) return null;
  return createPortal(
    <div className="discovery-overlay showing-request-overlay">
      <section ref={dialogRef} tabIndex={-1} data-assistant-surface="showing" className="discovery-dialog showing-request-dialog" role="dialog" aria-modal="true" aria-label="Request a showing">
        <button className="discovery-close" aria-label="Back to property details" onClick={close}>×</button>
        <header className="showing-request-header">
          <h2>Request a showing</h2>
          <p>Choose a preferred time. Your agent will confirm availability.</p>
        </header>
        {result ? <>
          <p role="status" className="discovery-answer showing-request-body">{result.message}</p>
          <div className="showing-request-actions"><button type="button" onClick={close}>Keep browsing</button></div>
        </> : <form className="showing-request-form" onSubmit={async e => {
          e.preventDefault();
          const result=await prepare();
          onResult?.(result);
        }}>
          <div className="showing-request-body">
            <div className="showing-request-fields">
              {Object.keys(empty).map(field => <label key={field}>{ {
                name: 'Full name', email: 'Email', phone: 'Phone', date: 'Preferred date', time: 'Preferred time'
              }[field]}<input required disabled={busy} type={field === 'phone' ? 'tel' : ['date', 'time', 'email'].includes(field) ? field : 'text'} value={draft[field]} min={field === 'date' ? config?.current_date : undefined} onChange={e => update({[field]: e.target.value}, true)} /></label>)}
            </div>
            <p className="showing-request-timezone">Time zone: {config?.timezone || 'Loading the agent’s time zone…'}</p>
            {review && <div className="discovery-answer showing-request-review">
              <strong>Review your request</strong>
              <p>{homeAddress(review.home)}<br />{review.date} at {review.time} · {review.timezone}<br />{review.name} · {review.email} · {review.phone}</p>
            </div>}
            {error && <p role="alert">{error}</p>}
          </div>
          <div className="showing-request-actions">
            <button type="button" onClick={close}>Skip for now</button>
            {review ? <button type="button" className="discovery-primary" disabled={busy} onClick={async () => {const result=await submit();onResult?.(result);}}>Confirm and send showing request</button>
              : <button type="submit" disabled={busy}>{busy ? 'Working…' : 'Review showing request'}</button>}
          </div>
        </form>}
      </section>
    </div>, document.body);
}
